/**
 * FIELDWEAR A/B Traffic Splitter
 * File: assets/fieldwear-ab.js
 *
 * Responsibilities:
 * 1. Assign visitor to a variant (persistent cookie)
 * 2. Write session to Supabase
 * 3. Expose window.__fwAB for all other scripts to read
 * 4. Apply variant class to <body> for CSS targeting
 * 5. Track: view, product_view, collection_view, atc, checkout_start,
 *            purchase, signup, scroll_depth, dwell, micro_interaction,
 *            quiz_reply, sms_reply, survey_reply, exit_intent
 * 6. Tag Microsoft Clarity with variant (segments heatmaps per variant)
 */

(function () {
  'use strict';

  // ── Config ────────────────────────────────────────────────
  // Injected from fw-ab-config.liquid via window.__fwConfig
  const CONFIG        = window.__fwConfig || {};
  const SUPABASE_URL  = CONFIG.supabaseUrl    || '';
  const SUPABASE_ANON = CONFIG.supabaseAnon   || '';
  const STORE_ID      = CONFIG.storeId        || '';
  const INSTALL_TOKEN = CONFIG.installToken   || '';
  const EXPERIMENT_ID = CONFIG.experimentId   || '';
  const VARIANT_A_ID  = CONFIG.variantAId     || '';
  const VARIANT_B_ID  = CONFIG.variantBId     || '';
  const TRAFFIC_SPLIT = CONFIG.trafficSplit   || 0.5;

  const COOKIE_NAME   = 'fw_ab_session';
  const COOKIE_DAYS   = 30;

  // Signal weights — how much each event contributes to engagement score
  const WEIGHTS = {
    view:              0.05,
    product_view:      0.15,
    collection_view:   0.10,
    atc:               0.65,
    checkout_start:    0.80,
    purchase:          0.99,
    signup:            0.70,
    quiz_reply:        0.90,
    sms_reply:         0.85,
    survey_reply:      0.95,
    chat_end:          0.75,
    exit_intent:       0.60,
    scroll_depth:      0.30,
    dwell:             0.25,
    micro_interaction: 0.20,
  };

  const SOURCES = {
    quiz_reply:        'zero_party',
    sms_reply:         'zero_party',
    survey_reply:      'zero_party',
    signup:            'first_party',
    chat_end:          'first_party',
    atc:               'first_party',
    checkout_start:    'first_party',
    purchase:          'first_party',
    exit_intent:       'first_party',
    view:              'behavioral',
    product_view:      'behavioral',
    collection_view:   'behavioral',
    scroll_depth:      'behavioral',
    dwell:             'behavioral',
    micro_interaction: 'behavioral',
  };

  // ── Utilities ─────────────────────────────────────────────

  function uuidv4() {
    if (crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  async function sha256(str) {
    if (!crypto.subtle) return str;
    var buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
    return Array.from(new Uint8Array(buf)).map(function(b){ return b.toString(16).padStart(2, '0'); }).join('');
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=/; SameSite=Lax';
  }

  function getUTM() {
    var p = new URLSearchParams(window.location.search);
    return {
      source:   p.get('utm_source')   || null,
      medium:   p.get('utm_medium')   || null,
      campaign: p.get('utm_campaign') || null,
    };
  }

  // ── Variant assignment ────────────────────────────────────

  function assignVariant() {
    var urlParams = new URLSearchParams(window.location.search);
    var forced = urlParams.get('fw_variant');
    if (forced === 'a') return { variantId: VARIANT_A_ID, role: 'control' };
    if (forced === 'b') return { variantId: VARIANT_B_ID, role: 'challenger' };

    return Math.random() < TRAFFIC_SPLIT
      ? { variantId: VARIANT_B_ID, role: 'challenger' }
      : { variantId: VARIANT_A_ID, role: 'control' };
  }

  // ── Session management ────────────────────────────────────

  async function getOrCreateSession() {
    var cookieData = null;
    try {
      var raw = getCookie(COOKIE_NAME);
      if (raw) cookieData = JSON.parse(raw);
    } catch (_) {}

    if (cookieData && cookieData.sessionId && cookieData.variantId) {
      return cookieData;
    }

    var rawId   = uuidv4();
    var token   = await sha256(rawId);
    var variant = assignVariant();
    var session = {
      sessionId:  rawId,
      token:      token,
      variantId:  variant.variantId,
      role:       variant.role,
      assignedAt: Date.now(),
    };

    setCookie(COOKIE_NAME, JSON.stringify(session), COOKIE_DAYS);
    return session;
  }

  // ── Supabase writes ───────────────────────────────────────

  async function writeSession(session) {
    if (!SUPABASE_URL || !SUPABASE_ANON || !EXPERIMENT_ID || !STORE_ID) return;
    var utm = getUTM();
    try {
      await fetch(SUPABASE_URL + '/rest/v1/sessions', {
        method: 'POST',
        headers: {
          'Content-Type':    'application/json',
          'apikey':           SUPABASE_ANON,
          'Authorization':   'Bearer ' + SUPABASE_ANON,
          'X-FW-Store-Token': INSTALL_TOKEN,
          'Prefer':          'return=minimal',
        },
        body: JSON.stringify({
          id:            session.sessionId,
          session_token: session.token,
          store_id:      STORE_ID,
          experiment_id: EXPERIMENT_ID,
          variant_id:    session.variantId,
          referrer:      document.referrer || null,
          page_url:      window.location.href,
          utm_source:    utm.source,
          utm_medium:    utm.medium,
          utm_campaign:  utm.campaign,
        }),
      });
    } catch (err) {
      console.warn('[FW AB] Session write failed:', err.message);
    }
  }

  async function writeEvent(eventType, payload, weight) {
    var ab = window.__fwAB;
    if (!ab || !SUPABASE_URL || !SUPABASE_ANON) return;
    payload = payload || {};
    var signalWeight = (weight != null) ? weight : (WEIGHTS[eventType] || 0.1);

    try {
      await fetch(SUPABASE_URL + '/rest/v1/events', {
        method: 'POST',
        headers: {
          'Content-Type':    'application/json',
          'apikey':           SUPABASE_ANON,
          'Authorization':   'Bearer ' + SUPABASE_ANON,
          'X-FW-Store-Token': INSTALL_TOKEN,
          'Prefer':          'return=minimal',
        },
        body: JSON.stringify({
          session_id:    ab.sessionId,
          variant_id:    ab.variantId,
          experiment_id: EXPERIMENT_ID,
          store_id:      STORE_ID,
          event_type:    eventType,
          signal_source: SOURCES[eventType] || 'behavioral',
          signal_weight: signalWeight,
          payload:       payload,
          page_url:      window.location.href,
        }),
      });
    } catch (err) {
      console.warn('[FW AB] Event write failed:', err.message);
    }
  }

  // ── Cart attribute — webhook session correlation ──────────

  async function writeCartAttribute(sessionId) {
    try {
      await fetch('/cart/update.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ attributes: { fw_session_id: sessionId } }),
      });
    } catch (_) {}
  }

  // ── DOM variant application ───────────────────────────────

  function applyVariant(session) {
    document.body.classList.add('fw-variant-' + session.role);
    document.body.dataset.fwVariant = session.variantId;

    var productPage = document.querySelector('.fw-product-page');
    if (productPage) {
      productPage.dataset.abVariant = session.variantId;
      productPage.dataset.abRole    = session.role;
    }
  }

  // ── Microsoft Clarity — variant tagging ──────────────────
  // Segments heatmaps, click maps, and session recordings by A/B variant.
  // Install Clarity via https://apps.shopify.com/microsoft-clarity
  // then paste your Clarity project ID into fw-ab-config.liquid as clarityId.

  function tagClarity(role) {
    var clarityId = CONFIG.clarityId;
    if (!clarityId) return; // no Clarity configured

    function doTag() {
      if (typeof window.clarity === 'function') {
        window.clarity('set', 'fw_variant', role);       // 'control' or 'challenger'
        window.clarity('set', 'fw_experiment', EXPERIMENT_ID.slice(0, 8));
      }
    }

    // Clarity loads async — retry on load if not ready yet
    if (window.clarity) {
      doTag();
    } else {
      window.addEventListener('load', doTag, { once: true });
    }
  }

  // ── Page-type events ──────────────────────────────────────

  function firePageTypeEvents() {
    var cls = document.body.className;

    // Product page view
    if (cls.indexOf('template-product') !== -1) {
      writeEvent('product_view', {
        page:    window.location.pathname,
        product: document.title,
      });
    }

    // Collection page view
    if (cls.indexOf('template-collection') !== -1) {
      writeEvent('collection_view', {
        page:       window.location.pathname,
        collection: document.title,
      });
    }

    // Generic page view (all pages)
    writeEvent('view', {
      page:     window.location.pathname,
      referrer: document.referrer || null,
    });
  }

  // ── Email signup tracking ─────────────────────────────────

  function hookEmailSignups() {
    // Fires when any email capture form submits (newsletter, popup, footer, etc.)
    // Excludes checkout forms to avoid false positives.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form) return;
      var hasEmail = form.querySelector('input[type="email"], input[name="contact[email]"]');
      var isCheckout = form.action && (
        form.action.indexOf('checkout') !== -1 ||
        form.action.indexOf('account') !== -1
      );
      if (hasEmail && !isCheckout) {
        writeEvent('signup', { page: window.location.pathname });
      }
    }, true); // capture phase — fires before Shopify's own handlers
  }

  // ── Checkout start tracking ───────────────────────────────

  function hookCheckoutStart() {
    // Fires when visitor clicks any checkout button.
    // Uses event delegation so it works with dynamically rendered cart drawers.
    var tracked = false; // dedupe per page load
    document.addEventListener('click', function (e) {
      if (tracked) return;
      var btn = e.target.closest(
        '[name="checkout"], .cart__checkout-button, #checkout, ' +
        'button[data-action="checkout"], a[href="/checkout"]'
      );
      if (btn) {
        tracked = true;
        writeEvent('checkout_start', { page: window.location.pathname });
      }
    }, true);
  }

  // ── ATC + purchase hooks (Shopify Dawn) ──────────────────

  function hookShopifyEvents() {
    // ATC via Dawn pub/sub
    if (typeof subscribe === 'function') {
      subscribe('cart-update', function () {
        writeEvent('atc', { page: window.location.pathname });
      });
    }

    // Purchase — available on /thank_you page
    if (window.Shopify && window.Shopify.checkout) {
      var checkout = window.Shopify.checkout;
      if (checkout.order_id) {
        writeEvent('purchase', {
          order_id:    checkout.order_id,
          total_price: checkout.total_price,
          currency:    checkout.currency,
        }, 0.99);
      }
    }
  }

  // ── Init ──────────────────────────────────────────────────

  async function init() {
    var session = await getOrCreateSession();

    // Expose globally — tracker and other scripts call window.__fwAB.writeEvent(...)
    window.__fwAB = {
      sessionId:    session.sessionId,
      variantId:    session.variantId,
      role:         session.role,
      experimentId: EXPERIMENT_ID,
      writeEvent:   writeEvent,
    };

    applyVariant(session);
    tagClarity(session.role);

    // Write session row first, then fire page events (avoids FK race)
    await writeSession(session);

    writeCartAttribute(session.sessionId);
    firePageTypeEvents();
    hookEmailSignups();
    hookCheckoutStart();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', hookShopifyEvents);
    } else {
      hookShopifyEvents();
    }
  }

  // Boot — runs on DOMContentLoaded (deferred script)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
